import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — Recursos y Datos, `/api/private-records` y `/api/data-forms`.
 * Módulo 15.
 *
 * **Treinta y siete llamadas**, el contrato más grande del portal, y todas son
 * las del vanilla. Lo único que cambia es que **la tabla de registros ordena en
 * el servidor**: `list_entries` acepta `sort` y `order` desde que existe, y la
 * pantalla no los usaba —ordenaba el array en memoria y acto seguido volvía a
 * pedir la tabla, que llegaba en el orden original—.
 */

/* ── Campos ────────────────────────────────────────────────────────────── */

export type TipoBasico =
  | 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'employee';

export type TipoInteligente =
  | 'tiempo_transcurrido' | 'vencimiento' | 'estado' | 'calculado' | 'formula'
  | 'relacion' | 'indicador' | 'progreso' | 'vacaciones';

export type TipoDeCampo = TipoBasico | TipoInteligente;

export const TIPOS_BASICOS: Array<[TipoBasico, string]> = [
  ['text', 'Texto'], ['textarea', 'Párrafo'], ['number', 'Número'], ['date', 'Fecha'],
  ['email', 'Correo'], ['phone', 'Teléfono'], ['select', 'Lista'], ['employee', 'Empleado'],
];

export const TIPOS_INTELIGENTES: Array<[TipoInteligente, string]> = [
  ['tiempo_transcurrido', 'Tiempo transcurrido'], ['vencimiento', 'Vencimiento'],
  ['estado', 'Estado inteligente'], ['calculado', 'Calculado'], ['formula', 'Fórmula'],
  ['relacion', 'Relación'], ['indicador', 'Indicador'], ['progreso', 'Progreso'],
  ['vacaciones', 'Vacaciones'],
];

const CLAVES_INTELIGENTES = new Set<string>(TIPOS_INTELIGENTES.map(([k]) => k));

export const esInteligente = (t: string | undefined): boolean => CLAVES_INTELIGENTES.has(t ?? '');

export interface Campo {
  key: string;
  label: string;
  type: TipoDeCampo;
  required?: boolean;
  options?: string[];
  config?: Record<string, string | number | ReglaDeIndicador[] | undefined>;
  readonly?: boolean;
  /** Solo en la Base de Datos Empleados: ¿la llena el colaborador? */
  autogestion?: boolean;
  sensible?: boolean;
  catalog_source?: string;
  map_to?: string;
}

export interface ReglaDeIndicador {
  op: string;
  value: string;
  label: string;
}

/**
 * La clave de una columna, derivada de su etiqueta **igual que en el servidor**
 * (`_clave_desde_etiqueta`): las tildes se pliegan antes de sustituir.
 *
 * Si las dos no coinciden, «Observación interna» daría `observaci_n_interna`
 * aquí y `observación_interna` en Python, y los valores quedarían huérfanos
 * dentro de `entry.data`.
 */
export function clavePara(etiqueta: string): string {
  return (String(etiqueta || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)) || '';
}

/**
 * La clave con la que se compara un NOMBRE, igual que `norm_nombre` en el
 * servidor: sin mayúsculas, sin tildes y sin puntuación.
 *
 * Sirve para avisar de dos recursos que se llaman igual. El servidor ya los
 * rechaza al crearlos (revisión 2026-09-25), pero los que existían antes de esa
 * regla siguen ahí, y dos tarjetas idénticas en la rejilla no se pueden
 * distinguir sin abrirlas.
 */
export function claveDeNombre(nombre: string): string {
  return String(nombre || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** Las claves de nombre que aparecen más de una vez en la lista. */
export function nombresRepetidos(recursos: Array<{ name: string }>): Set<string> {
  const veces = new Map<string, number>();
  for (const r of recursos) {
    const k = claveDeNombre(r.name);
    veces.set(k, (veces.get(k) ?? 0) + 1);
  }
  return new Set([...veces].filter(([, n]) => n > 1).map(([k]) => k));
}

/* ── Recursos y registros ──────────────────────────────────────────────── */

export interface DatosSensibles {
  documento: boolean;
  autorizados: number;
  colaboradores: number;
}

export interface Recurso {
  id: number;
  name: string;
  description?: string | null;
  icon: string;
  color: string;
  is_expediente: boolean;
  is_system: boolean;
  protected_keys: string[];
  field_count: number;
  entry_count: number;
  fields?: Campo[];
  datos_sensibles?: DatosSensibles;
}

export interface Registro {
  id: number;
  resource_id: number;
  data: Record<string, string | number | null>;
  version: number;
  subject_user_id?: number | null;
  subject_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Registros {
  resource: Recurso;
  entries: Registro[];
  retirados_ocultos: number;
  incluye_retirados: boolean;
}

export interface Version {
  version: number;
  action: string;
  action_label: string;
  changed_fields: Array<{ label: string; old?: string | null; new?: string | null }>;
  reason?: string | null;
  changed_by: string;
  changed_at?: string | null;
}

export interface FuenteDeCatalogo {
  key: string;
  label: string;
  options?: string[];
}

export interface Empleado {
  id: number;
  label: string;
  sub?: string | null;
}

export interface Alerta {
  resource: string;
  resource_id: number;
  name: string;
  field: string;
  status: string;
  date?: string | null;
}

export interface Alertas {
  total: number;
  vencidos: number;
  por_vencer: number;
  items: Alerta[];
}

export interface RevisionDelMaestro {
  synced: number;
  errores: number;
  duplicados_consolidados: number;
  huerfanas?: { total: number; limpiadas?: unknown[] };
  expediente_sin_dueno?: Record<string, unknown[]>;
}

export interface Hijo {
  id: number;
  full_name: string;
  birth_date?: string | null;
  gender?: string | null;
  age?: number | null;
}

export interface Expediente {
  user: { id: number; full_name: string; cedula: string; email: string; hire_date?: string | null };
  resolved: Record<string, string | null>;
  tags: string[];
  total_records: number;
  domains: Array<Recurso & { entries: Registro[] }>;
}

/* ── Recolección de datos ──────────────────────────────────────────────── */

export interface Formulario {
  id: number;
  name: string;
  description?: string | null;
  icon: string;
  color: string;
  target_kind: string;
  target_label: string;
  target_resource_id?: number | null;
  target_resource_name?: string | null;
  approval_mode: 'manual' | 'auto';
  notify_emails: string;
  field_count: number;
  responses: Record<string, number>;
  total_responses: number;
  fields?: Campo[];
}

export interface DestinosDeFormulario {
  kinds: Array<{ key: string; label: string }>;
  resources: Array<{ id: number; name: string; icon: string }>;
}

export interface Invitacion {
  id: number;
  to: string;
  is_new_person: boolean;
  status: string;
  submitted_at?: string | null;
  expires_at?: string | null;
}

export interface Pendiente {
  id: number;
  who: string;
  form_name: string;
  submitted_at?: string | null;
  is_new_person: boolean;
}

export interface RespuestaParaRevisar {
  form_name: string;
  who: string;
  is_new_person: boolean;
  submitted_at?: string | null;
  fields: Array<{ label: string; current?: string | null; proposed?: string | null; changed: boolean }>;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export interface OrdenDeTabla {
  campo: string;
  sentido: 'asc' | 'desc';
}

export const claves = {
  todo: ['recursos'] as const,
  recursos: ['recursos', 'lista'] as const,
  recurso: (id: number) => ['recursos', 'recurso', id] as const,
  registros: (id: number, busqueda: string, retirados: boolean, orden: OrdenDeTabla | null) =>
    ['recursos', 'registros', id, busqueda, retirados, orden] as const,
  registro: (id: number) => ['recursos', 'registro', id] as const,
  versiones: (id: number) => ['recursos', 'versiones', id] as const,
  catalogos: (conOpciones: boolean) => ['recursos', 'catalogos', conOpciones] as const,
  alertas: ['recursos', 'alertas'] as const,
  expediente: (userId: number) => ['recursos', 'expediente', userId] as const,
  hijos: (userId: number) => ['recursos', 'hijos', userId] as const,
  empleados: (q: string) => ['recursos', 'empleados', q] as const,
  formularios: ['recursos', 'formularios'] as const,
  formulario: (id: number) => ['recursos', 'formulario', id] as const,
  destinos: ['recursos', 'destinos'] as const,
  invitaciones: (id: number) => ['recursos', 'invitaciones', id] as const,
  pendientes: ['recursos', 'pendientes'] as const,
  respuesta: (id: number) => ['recursos', 'respuesta', id] as const,
};

function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: claves.todo });
}

/* ── Lecturas: recursos ────────────────────────────────────────────────── */

export function useRecursos(habilitado = true) {
  return useQuery({
    queryKey: claves.recursos,
    queryFn: () => api.get<Recurso[]>('/private-records/resources'),
    enabled: habilitado,
  });
}

export function useRecurso(id: number | null) {
  return useQuery({
    queryKey: claves.recurso(id ?? 0),
    queryFn: () => api.get<Recurso>(`/private-records/resources/${id}`),
    enabled: id != null,
  });
}

export function useRegistros(
  id: number | null, busqueda: string, retirados: boolean, orden: OrdenDeTabla | null,
) {
  return useQuery({
    queryKey: claves.registros(id ?? 0, busqueda, retirados, orden),
    queryFn: () => api.get<Registros>(`/private-records/resources/${id}/entries`, {
      ...(busqueda ? { search: busqueda } : {}),
      ...(retirados ? { incluir_retirados: 'true' } : {}),
      // El servidor sabe ordenar desde que existe este endpoint.
      ...(orden ? { sort: orden.campo, order: orden.sentido } : {}),
    }),
    enabled: id != null,
    placeholderData: (previa) => previa,
  });
}

export function useRegistro(id: number | null) {
  return useQuery({
    queryKey: claves.registro(id ?? 0),
    queryFn: () => api.get<Registro & { subject_name?: string }>(`/private-records/entries/${id}`),
    enabled: id != null,
  });
}

export function useVersiones(id: number | null) {
  return useQuery({
    queryKey: claves.versiones(id ?? 0),
    queryFn: () => api.get<{ current_version: number; versions: Version[] }>(
      `/private-records/entries/${id}/versions`),
    enabled: id != null,
  });
}

export function useCatalogos(habilitado: boolean, conOpciones = false) {
  return useQuery({
    queryKey: claves.catalogos(conOpciones),
    queryFn: () => api.get<FuenteDeCatalogo[]>('/private-records/catalog-sources',
      conOpciones ? { opciones: 'true' } : {}),
    enabled: habilitado,
    staleTime: 10 * 60_000,
  });
}

export function useAlertas(habilitado: boolean) {
  return useQuery({
    queryKey: claves.alertas,
    queryFn: () => api.get<Alertas>('/private-records/alerts'),
    enabled: habilitado,
  });
}

export function useExpediente(userId: number | null) {
  return useQuery({
    queryKey: claves.expediente(userId ?? 0),
    queryFn: () => api.get<Expediente>(`/private-records/expediente/${userId}`),
    enabled: userId != null,
  });
}

/** Los hijos viven en su propia tabla, no en el motor de recursos: si su
 *  consulta falla, el expediente se sigue viendo. Por eso es una consulta
 *  aparte y no un `Promise.all` con `.catch(() => [])`, que era como el vanilla
 *  escondía el fallo. */
export function useHijos(userId: number | null) {
  return useQuery({
    queryKey: claves.hijos(userId ?? 0),
    queryFn: () => api.get<Hijo[]>(`/users/${userId}/children`),
    enabled: userId != null,
  });
}

export function useEmpleados(q: string) {
  const limpio = q.trim();
  return useQuery({
    queryKey: claves.empleados(limpio),
    queryFn: () => api.get<Empleado[]>('/private-records/autocomplete/employees', { q: limpio }),
    enabled: limpio.length >= 2,
  });
}

/* ── Lecturas: recolección ─────────────────────────────────────────────── */

export function useFormularios(habilitado: boolean) {
  return useQuery({
    queryKey: claves.formularios,
    queryFn: () => api.get<Formulario[]>('/data-forms/'),
    enabled: habilitado,
  });
}

export function useFormulario(id: number | null) {
  return useQuery({
    queryKey: claves.formulario(id ?? 0),
    queryFn: () => api.get<Formulario>(`/data-forms/${id}`),
    enabled: id != null,
  });
}

export function useDestinos(habilitado: boolean) {
  return useQuery({
    queryKey: claves.destinos,
    queryFn: () => api.get<DestinosDeFormulario>('/data-forms/targets'),
    enabled: habilitado,
    staleTime: 10 * 60_000,
  });
}

export function useInvitaciones(formId: number | null) {
  return useQuery({
    queryKey: claves.invitaciones(formId ?? 0),
    queryFn: () => api.get<Invitacion[]>(`/data-forms/${formId}/invitations`),
    enabled: formId != null,
  });
}

/** La bandeja de revisiones. Es un aviso, no el contenido: tiene su propio
 *  estado para que un fallo suyo no se lleve la pantalla por delante. */
export function usePendientes(habilitado: boolean) {
  return useQuery({
    queryKey: claves.pendientes,
    queryFn: () => api.get<Pendiente[]>('/data-forms/reviews/pending'),
    enabled: habilitado,
  });
}

export function useRespuesta(id: number | null) {
  return useQuery({
    queryKey: claves.respuesta(id ?? 0),
    queryFn: () => api.get<RespuestaParaRevisar>(`/data-forms/responses/${id}`),
    enabled: id != null,
  });
}

/* ── Escrituras: recursos ──────────────────────────────────────────────── */

export interface CuerpoDeRecurso {
  name: string;
  description: string | null;
  icon: string;
  color: string;
  fields: Campo[];
}

export function useGuardarRecurso() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: CuerpoDeRecurso }) =>
      v.id
        ? api.put<Recurso>(`/private-records/resources/${v.id}`, v.cuerpo)
        : api.post<Recurso>('/private-records/resources', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarRecurso() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/private-records/resources/${id}`),
    onSuccess: () => refrescar(),
  });
}

export interface CuerpoDeRegistro {
  data: Record<string, string>;
  reason: string | null;
  subject_user_id?: number;
}

export function useGuardarRegistro() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; recursoId: number; cuerpo: CuerpoDeRegistro }) =>
      v.id
        ? api.put<{ skipped?: Record<string, string> }>(`/private-records/entries/${v.id}`, v.cuerpo)
        : api.post<{ skipped?: Record<string, string> }>(
          `/private-records/resources/${v.recursoId}/entries`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarRegistro() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/private-records/entries/${id}`),
    onSuccess: () => refrescar(),
  });
}

export function useRestaurarVersion() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; version: number; reason: string | null }) =>
      api.post<{ message: string }>(
        `/private-records/entries/${v.id}/restore/${v.version}`, { reason: v.reason }),
    onSuccess: () => refrescar(),
  });
}

export function useEnviarPorCorreo() {
  return useMutation({
    mutationFn: (v: { id: number; to: string; message: string | null }) =>
      api.post<{ message: string }>(`/private-records/entries/${v.id}/email`,
        { to: v.to, message: v.message }),
  });
}

export function useImportar() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { recursoId: number; archivo: File }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      return api.upload<{
        created: number; errors: number; mapped_columns: string[];
        detail?: Array<{ fila: number; error: string }>;
      }>(`/private-records/resources/${v.recursoId}/import`, datos);
    },
    onSuccess: () => refrescar(),
  });
}

export function useRevisarMaestro() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (limpiar: boolean) =>
      api.post<RevisionDelMaestro>(
        `/private-records/employee-db/sync-all${limpiar ? '?limpiar=true' : ''}`, null),
    onSuccess: () => refrescar(),
  });
}

export function useSembrarExpediente() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: () => api.post<{ created: string[] }>('/private-records/expediente/seed', {}),
    onSuccess: () => refrescar(),
  });
}

export function useEnviarAlertas() {
  return useMutation({
    mutationFn: () => api.post<{ message: string }>('/private-records/alerts/send', {}),
  });
}

export function useAgregarHijo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { userId: number; cuerpo: { full_name: string; birth_date?: string; gender?: string } }) =>
      api.post<Hijo>(`/users/${v.userId}/children`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useQuitarHijo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (childId: number) => api.del<{ message: string }>(`/users/children/${childId}`),
    onSuccess: () => refrescar(),
  });
}

/* ── Escrituras: recolección ───────────────────────────────────────────── */

export interface CuerpoDeFormulario {
  name: string;
  description: string | null;
  icon: string;
  color: string;
  target_kind: string;
  target_resource_id: number | null;
  approval_mode: 'manual' | 'auto';
  notify_emails: string | null;
  fields: Campo[];
}

export function useGuardarFormulario() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: CuerpoDeFormulario }) =>
      v.id
        ? api.put<Formulario>(`/data-forms/${v.id}`, v.cuerpo)
        : api.post<Formulario>('/data-forms/', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarFormulario() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/data-forms/${id}`),
    onSuccess: () => refrescar(),
  });
}

export function useCamposSugeridos() {
  return useMutation({
    mutationFn: (v: { target_kind: string; target_resource_id?: number | null }) =>
      api.get<{ fields: Campo[] }>('/data-forms/suggested-fields', {
        target_kind: v.target_kind,
        ...(v.target_resource_id ? { target_resource_id: v.target_resource_id } : {}),
      }),
  });
}

export function useInvitar() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: {
      formId: number;
      cuerpo: { user_ids: number[]; emails: string | null; expires_days: number; send_email: boolean };
    }) => api.post<{ created: number; expires: string; links: Array<{ to: string; link: string }> }>(
      `/data-forms/${v.formId}/invitations`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useRecordarPendientes() {
  return useMutation({
    mutationFn: (formId: number) =>
      api.post<{ message: string }>(`/data-forms/${formId}/remind`, {}),
  });
}

export function useDecidir() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; decision: 'aprobar' | 'devolver' | 'rechazar'; note: string | null }) =>
      api.post<{ status: string; temp_password?: string; email_skipped?: string }>(
        `/data-forms/responses/${v.id}/decide`, { decision: v.decision, note: v.note }),
    onSuccess: () => refrescar(),
  });
}

/* ── Descargas ─────────────────────────────────────────────────────────── */

/** Export e importación de plantilla: las dos van por `Utils.downloadAuthed`,
 *  que lleva el token en la cabecera. El vanilla exportaba con un `fetch` y el
 *  token escrito a mano, teniendo esto tres líneas más abajo. */
export function exportar(recursoId: number, formato: 'xlsx' | 'pdf', retirados: boolean) {
  const q = retirados ? '?incluir_retirados=true' : '';
  return Utils.downloadAuthed(
    `/api/private-records/resources/${recursoId}/export/${formato}${q}`, '');
}

export function descargarPlantilla(recursoId: number) {
  return Utils.downloadAuthed(
    `/api/private-records/resources/${recursoId}/import/template`, '');
}
